// src/components/LibraryCapsuleCard.tsx
import { Link } from 'react-router-dom';
import { Capsule, LAYER_ORDER } from '../types/capsule';
import { getDifficultyLabel, getDifficultyStars } from '../features/capsules/utils/capsuleFormat';
import '../styles/LibraryCapsuleCard.css';

interface LibraryCapsuleCardProps {
  capsule: Capsule;
  isBookmarked: boolean;
  onToggleBookmark: (slug: string) => void;
  isLocked?: boolean;
}

export function LibraryCapsuleCard({
  capsule,
  isBookmarked,
  onToggleBookmark,
  isLocked = false,
}: LibraryCapsuleCardProps) {
  const { metadata, layers, slug } = capsule;
  const difficulty = metadata.difficulty ?? 1;
  const stars = getDifficultyStars(difficulty);
  const tags = metadata.tags?.slice(0, 2) || [];

  const handleBookmarkClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleBookmark(slug);
  };

  return (
    <Link
      to={`/capsule/${slug}`}
      className={`library-capsule-card ${isBookmarked ? 'bookmarked' : ''}`}
      data-testid="capsule-card-link"
    >
      {/* Thumbnail placeholder */}
      <div className="card-thumbnail">
        {isLocked && (
          <div className="card-lock-overlay">
            <svg className="card-lock-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
        )}
      </div>

      <div className="card-body">
        {/* Title and Artist */}
        <h3 className="card-title">{metadata.title}</h3>
        {metadata.artist && <p className="card-artist">{metadata.artist}</p>}

        {/* Difficulty */}
        <div className="card-difficulty">
          <span className="card-stars">{stars}</span>
          <span className="card-difficulty-label">{getDifficultyLabel(difficulty)}</span>
        </div>

        {/* Tags */}
        {tags.length > 0 && (
          <div className="card-tags">
            {tags.map((tag) => (
              <span key={tag} className="card-tag">{tag}</span>
            ))}
          </div>
        )}

        {/* Layer dots */}
        <div className="card-layers">
          {LAYER_ORDER.map((layer) => {
            const hasLayer = layers[layer] !== undefined;
            return (
              <span
                key={layer}
                className={`card-layer-dot ${hasLayer ? 'filled' : 'empty'}`}
                data-testid={hasLayer ? 'layer-dot-filled' : 'layer-dot-empty'}
                title={layer}
              />
            );
          })}
        </div>

        {/* Footer with bookmark */}
        <div className="card-footer">
          <div /> {/* Spacer */}
          <button
            className={`bookmark-button ${isBookmarked ? 'bookmarked' : ''}`}
            onClick={handleBookmarkClick}
            data-testid="bookmark-button"
            aria-label={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
          >
            <svg className="bookmark-icon" viewBox="0 0 24 24" fill={isBookmarked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
              <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
            </svg>
          </button>
        </div>
      </div>
    </Link>
  );
}
